import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import AssessmentIntro from './components/AssessmentIntro';
import AssessmentProgress from './components/AssessmentProgress';
import QuestionCard from './components/QuestionCard';
import EmergencyAssessment from './components/EmergencyAssessment';
import AssessmentResults from './components/AssessmentResults';
import Icon from '../../components/AppIcon';
import logo from '../../../public/assets/images/fibrelogo.png';



const NetworkAssessmentTool = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState('intro');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  const [assessmentResults, setAssessmentResults] = useState(null);

  // Mock assessment questions
  const questions = [
    {
      id: 'business_type',
      title: 'What type of business do you operate?',
      description: 'This helps us understand your specific connectivity requirements and industry standards.',
      type: 'select',
      icon: 'Building',
      options: [
        { value: 'healthcare', label: 'Healthcare & Medical' },
        { value: 'finance', label: 'Financial Services' },
        { value: 'education', label: 'Education & Training' },
        { value: 'manufacturing', label: 'Manufacturing' },
        { value: 'retail', label: 'Retail & E-commerce' },
        { value: 'technology', label: 'Technology & Software' },
        { value: 'professional', label: 'Professional Services' },
        { value: 'other', label: 'Other' }
      ],
      placeholder: 'Select your business type'
    },
    {
      id: 'employee_count',
      title: 'How many employees work at your location?',
      description: 'Employee count helps determine bandwidth requirements and network capacity needs.',
      type: 'select',
      icon: 'Users',
      options: [
        { value: '1-10', label: '1-10 employees' },
        { value: '11-25', label: '11-25 employees' },
        { value: '26-50', label: '26-50 employees' },
        { value: '51-100', label: '51-100 employees' },
        { value: '101-250', label: '101-250 employees' },
        { value: '251-500', label: '251-500 employees' },
        { value: '500+', label: '500+ employees' }
      ],
      placeholder: 'Select employee count range'
    },
    {
      id: 'current_issues',
      title: 'What network issues are you currently experiencing?',
      description: 'Select all issues that apply to your current network situation.',
      type: 'multiselect',
      icon: 'AlertTriangle',
      options: [
        { value: 'slow_speeds', label: 'Slow internet speeds' },
        { value: 'frequent_outages', label: 'Frequent connection outages' },
        { value: 'video_quality', label: 'Poor video conferencing quality' },
        { value: 'file_transfer', label: 'Slow file transfers' },
        { value: 'cloud_access', label: 'Slow cloud application access' },
        { value: 'security_concerns', label: 'Security vulnerabilities' },
        { value: 'scalability', label: 'Cannot support growth' },
        { value: 'none', label: 'No current issues' }
      ]
    },
    {
      id: 'current_speed',
      title: 'What is your current internet speed?',
      description: 'If unsure, you can check your speed at speedtest.net or similar services.',
      type: 'select',
      icon: 'Gauge',
      options: [
        { value: 'under_25', label: 'Under 25 Mbps' },
        { value: '25_50', label: '25-50 Mbps' },
        { value: '50_100', label: '50-100 Mbps' },
        { value: '100_250', label: '100-250 Mbps' },
        { value: '250_500', label: '250-500 Mbps' },
        { value: '500_1000', label: '500 Mbps - 1 Gbps' },
        { value: 'over_1000', label: 'Over 1 Gbps' },
        { value: 'unknown', label: 'Not sure' }
      ],
      placeholder: 'Select your current speed'
    },
    {
      id: 'infrastructure_type',
      title: 'What type of internet connection do you currently have?',
      description: 'Understanding your current infrastructure helps us recommend the best upgrade path.',
      type: 'select',
      icon: 'Cable',
      options: [
        { value: 'copper_dsl', label: 'Copper/DSL' },
        { value: 'cable', label: 'Cable Internet' },
        { value: 'fiber', label: 'Fiber Optic' },
        { value: 'satellite', label: 'Satellite' },
        { value: 'wireless', label: 'Fixed Wireless' },
        { value: 'mixed', label: 'Mixed/Multiple Types' },
        { value: 'unknown', label: 'Not sure' }
      ],
      placeholder: 'Select your connection type'
    },
    {
      id: 'reliability_importance',
      title: 'How critical is network reliability for your business?',
      description: 'This helps us understand your uptime requirements and redundancy needs.',
      type: 'select',
      icon: 'Shield',
      options: [
        { value: 'mission_critical', label: 'Mission Critical - 99.9%+ uptime required' },
        { value: 'very_important', label: 'Very Important - Outages cause significant impact' },
        { value: 'important', label: 'Important - Some tolerance for brief outages' },
        { value: 'moderate', label: 'Moderate - Can handle occasional downtime' }
      ],
      placeholder: 'Select reliability importance'
    },
    {
      id: 'future_needs',
      title: 'What are your future bandwidth requirements?',
      description: 'Consider planned growth, new applications, or increased data usage.',
      type: 'multiselect',
      icon: 'TrendingUp',
      options: [
        { value: 'video_conferencing', label: 'More video conferencing' },
        { value: 'cloud_migration', label: 'Cloud migration/expansion' },
        { value: 'remote_work', label: 'Support for remote workers' },
        { value: 'iot_devices', label: 'IoT and smart devices' },
        { value: 'data_backup', label: 'Increased data backup needs' },
        { value: 'new_locations', label: 'Additional office locations' },
        { value: 'staff_growth', label: 'Significant staff growth' },
        { value: 'no_change', label: 'No significant changes planned' }
      ]
    },
    {
      id: 'timeline',
      title: 'What is your preferred timeline for network upgrades?',
      description: 'Understanding your timeline helps us prioritize and plan the implementation.',
      type: 'select',
      icon: 'Calendar',
      options: [
        { value: 'immediate', label: 'Immediate - ASAP' },
        { value: '1_month', label: 'Within 1 month' },
        { value: '3_months', label: 'Within 3 months' },
        { value: '6_months', label: 'Within 6 months' },
        { value: '1_year', label: 'Within 1 year' },
        { value: 'planning', label: 'Just planning/researching' }
      ],
      placeholder: 'Select your preferred timeline'
    },
    {
      id: 'budget_range',
      title: 'What is your approximate budget range for network infrastructure?',
      description: 'This helps us recommend solutions that fit your investment parameters.',
      type: 'select',
      icon: 'DollarSign',
      options: [
        { value: 'under_5k', label: 'Under $5,000' },
        { value: '5k_15k', label: '$5,000 - $15,000' },
        { value: '15k_30k', label: '$15,000 - $30,000' },
        { value: '30k_50k', label: '$30,000 - $50,000' },
        { value: '50k_100k', label: '$50,000 - $100,000' },
        { value: 'over_100k', label: 'Over $100,000' },
        { value: 'flexible', label: 'Flexible based on ROI' }
      ],
      placeholder: 'Select your budget range'
    },
    {
      id: 'additional_info',
      title: 'Any additional information about your network needs?',
      description: 'Share any specific requirements, concerns, or goals not covered above.',
      type: 'textarea',
      icon: 'MessageSquare',
      placeholder: 'Describe any specific requirements, current pain points, or goals for your network infrastructure...'
    }
  ];

  // Mock results calculation
  const calculateResults = () => {
    const mockResults = {
      overallScore: 65,
      performanceScore: 45,
      reliabilityScore: 70,
      futureReadyScore: 80,
      findings: [
        {
          type: 'critical',
          title: 'Bandwidth Bottleneck Detected',
          description: 'Your current connection speed is insufficient for your business size and requirements, causing productivity losses.'
        },
        {
          type: 'warning',
          title: 'Scalability Concerns',
          description: 'Current infrastructure cannot support planned growth and future technology requirements.'
        },
        {
          type: 'positive',
          title: 'Good Security Practices',
          description: 'Your current security measures are adequate but could benefit from fiber-based enhancements.'
        }
      ],
      recommendations: [
        {
          title: 'Fiber Optic Conversion',
          description: 'Upgrade to dedicated fiber connection with 1 Gbps symmetrical speeds',
          estimatedCost: '$25,000 - $35,000',
          timeline: '4-6 weeks',
          roi: '18 months',
          priority: 'High'
        },
        {
          title: 'Network Infrastructure Upgrade',
          description: 'Modernize internal network equipment to support fiber speeds',
          estimatedCost: '$8,000 - $12,000',
          timeline: '2-3 weeks',
          roi: '12 months',
          priority: 'Medium'
        },
        {
          title: 'Redundancy Implementation',
          description: 'Add backup connection for business continuity',
          estimatedCost: '$5,000 - $8,000',
          timeline: '3-4 weeks',
          roi: '24 months',
          priority: 'Medium'
        }
      ],
      roi: {
        productivity: '+35%',
        costSavings: '$18,000',
        paybackPeriod: '18 months'
      }
    };
    
    setAssessmentResults(mockResults);
    setCurrentStep('results');
  };

  const handleStartAssessment = () => {
    setCurrentStep('assessment');
    setCurrentQuestionIndex(0);
  };

  const handleEmergencyAssessment = () => {
    setShowEmergencyModal(true);
  };

  const handleEmergencySubmit = (formData) => {
    console.log('Emergency assessment submitted:', formData);
    setShowEmergencyModal(false);
    // In real app, this would trigger immediate contact
  };

  const handleAnswerChange = (answer) => {
    setAnswers(prev => ({
      ...prev,
      [questions?.[currentQuestionIndex]?.id]: answer
    }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions?.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      calculateResults();
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleRestart = () => {
    setCurrentStep('intro');
    setCurrentQuestionIndex(0);
    setAnswers({});
    setAssessmentResults(null);
  };

  const handleGetQuote = () => {
    navigate('/get-quote-consultation');
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep, currentQuestionIndex]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {currentStep === 'intro' && (
            <AssessmentIntro
              onStart={handleStartAssessment}
              onEmergencyAssessment={handleEmergencyAssessment}
            />
          )}

          {currentStep === 'assessment' && (
            <div className="space-y-6">
              <AssessmentProgress
                currentStep={currentQuestionIndex + 1}
                totalSteps={questions?.length}
                completedSteps={currentQuestionIndex}
              />
              
              <QuestionCard
                question={questions?.[currentQuestionIndex]}
                answer={answers?.[questions?.[currentQuestionIndex]?.id]}
                onAnswerChange={handleAnswerChange}
                onNext={handleNext}
                onPrevious={handlePrevious}
                isFirst={currentQuestionIndex === 0}
                isLast={currentQuestionIndex === questions?.length - 1}
              />
            </div>
          )}

          {currentStep === 'results' && assessmentResults && (
            <AssessmentResults
              results={assessmentResults}
              onRestart={handleRestart}
              onGetQuote={handleGetQuote}
            />
          )}
        </div>
      </main>
      {/* Emergency Assessment Modal */}
      {showEmergencyModal && (
        <EmergencyAssessment
          onClose={() => setShowEmergencyModal(false)}
          onSubmit={handleEmergencySubmit}
        />
      )}
      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                      <div className="grid md:grid-cols-4 gap-8">
                        {/* Company Info */}
                        <div className="space-y-4">
                          <div className="flex items-center space-x-3">
                            <img
                                            src={logo}
                                                alt="Xtreem Fibre Solutions"
                                                width={80}
                                                height={80}
                                                className="h-16 w-auto brightness-0 invert transition-transform duration-300 group-hover:scale-105"/>
                                            
                            <div>
                              <h3 className="text-lg font-bold">Xtreem Fibre Solutions</h3>
                              <p className="text-xs text-slate-400 font-mono">Infrastructure Transformation</p>
                            </div>
                          </div>
                          <p className="text-slate-300 text-sm leading-relaxed">
                            Transforming businesses through future-ready fiber optic infrastructure. 
                            Seamless transitions, minimal disruption, maximum performance.
                          </p>
                        </div>
            
                        {/* Services */}
                        <div>
                          <h4 className="font-semibold mb-4">Services</h4>
                          <ul className="space-y-2 text-sm text-slate-300">
                              <li><a href="/copper-to-fiber-conversion-service" className="hover:text-white transition-colors">Fibre Optics</a></li>
                            <li><a href="/network-assessment-tool" className="hover:text-white transition-colors">Technical Support</a></li>
                            <li><a href="#" className="hover:text-white transition-colors">Emergency Support</a></li>
                            <li><a href="#" className="hover:text-white transition-colors">Maintenance Plans</a></li>
                          </ul>
                        </div>
            
                        {/* Company */}
                        <div>
                          <h4 className="font-semibold mb-4">Company</h4>
                          <ul className="space-y-2 text-sm text-slate-300">              
                            <li><a href="/about-expertise" className="hover:text-white transition-colors">About Us</a></li>
                            <li><a href="/service-areas-local-teams" className="hover:text-white transition-colors">Service Areas</a></li>
                            <li><a href="/get-quote-consultation" className="hover:text-white transition-colors">Contact Us</a></li>                
                          </ul>
                        </div>
            
                        {/* Contact */}
                        <div>
                          <h4 className="font-semibold mb-4">Contact</h4>
                          <div className="space-y-3 text-sm text-slate-300">
                            <div>
                              <p className="font-medium text-white">Emergency: 24/7</p>
                              <p>01483 343 064</p>
                            </div>
                            <div>
                              <p className="font-medium text-white">General Inquiries</p>
                              <p>admin@xtreemfibresolutions.co.uk</p>
                            </div>
                            <div>
                              <p className="font-medium text-white">Business Hours</p>
                              <p>Mon-Fri: 8AM-6PM EST</p>
                            </div>
                          </div>
                        </div>
                      </div>
            
                      <div className="border-t border-slate-700 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
                        <p className="text-slate-400 text-sm">
                          © {new Date()?.getFullYear()} Xtreem Fibre Solutions. All rights reserved.
                        </p>
                        <div className="flex space-x-6 mt-4 md:mt-0">
                          {/* <a href="#" className="text-slate-400 hover:text-white text-sm transition-colors">Privacy Policy</a> */}
                          <a href="#" className="text-slate-400 hover:text-white text-sm transition-colors">Terms of Service</a>
                          {/* <a href="#" className="text-slate-400 hover:text-white text-sm transition-colors">Cookie Policy</a> */}
                        </div>
                      </div>
                    </div>
                  </footer>
    </div>
  );
};

export default NetworkAssessmentTool;